import { useEffect, useRef, type CSSProperties } from "react"

import styles from "./CymaticCloud.module.css"

/*
 * Cymatic particle cloud ported from the www repo's homepage hero
 * (CymaticScroll.astro) — the "Find signal in the noise" figure. Thousands of
 * gray particles settle onto the nodal lines of a Chladni standing wave on a
 * circular plate; a handful of red "error" markers lock onto the pattern, and
 * pass/fail metric chips cycle over it one at a time. On the marketing site the
 * chaos → resolved transition is scroll-driven; here there is no scroll, so the
 * figure renders in its resolved state and just lives (jitter + cursor push).
 */

// rx/ry position each chip on the cymatic nodal grid; pass drives the chip color.
const METRICS_A = [
  { rx: 0.5, ry: 0.28, label: "LATENCY 3.2S", pass: false },
  { rx: 0.364, ry: 0.31, label: "ESCALATION TRIGGERED", pass: true },
  { rx: 0.636, ry: 0.27, label: "AUDIO GLITCH", pass: false },
  { rx: 0.28, ry: 0.364, label: "AGENT FAILED TO RESPOND", pass: false },
  { rx: 0.68, ry: 0.364, label: "ISSUE RESOLVED", pass: true },
  { rx: 0.24, ry: 0.636, label: "SENTIMENT NEGATIVE", pass: false },
  { rx: 0.5, ry: 0.68, label: "IDENTITY VERIFIED", pass: true },
  { rx: 0.364, ry: 0.74, label: "RESERVATION BOOKED", pass: true },
  { rx: 0.58, ry: 0.58, label: "COMPLIANCE FAILURE", pass: false },
]

export function CymaticCloud() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const calloutsRef = useRef<HTMLDivElement>(null)
  const hoverBadgeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const section = sectionRef.current
    const canvas = canvasRef.current
    const calloutsA = calloutsRef.current
    const hoverBadge = hoverBadgeRef.current
    if (!section || !canvas || !calloutsA || !hoverBadge) {
      return
    }

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      return
    }

    // Resolved-state zoom of the cymatic square. Mirrored by the CSS
    // .calloutsInner multiplier so the snapped chips keep tracking the pattern.
    const END_SCALE = 1.15

    const CX = 550,
      CY = 550,
      SQUARE_HALF = 330 // SQUARE_HALF doubles as the disk radius
    // Chladni mode for the resolved figure (matches the ai4 landing hero).
    const MODE_M = 5,
      MODE_N = 1
    const GRID_STEP = 4
    // Perpendicular distance (1100-unit space) within which a grid cell counts
    // as on the nodal line. |v|/|∇v| keeps line thickness constant.
    const DIST_THRESHOLD = 2.625

    // Xorshift32 PRNG — deterministic layout.
    let seed = 0x9e3779b9
    const rand = () => {
      seed ^= seed << 13
      seed ^= seed >> 17
      seed ^= seed << 5
      return (seed >>> 0) / 0x100000000
    }

    // Chladni figure on a circular plate. The square-plate standing-wave
    // superposition sin(πN·u)·sin(πM·v) + sin(πM·u)·sin(πN·v) is evaluated over
    // the unit disk and clipped to it, then a rim ring is added so the figure
    // reads as a circle. Grid-scan + |v|/|∇v| keeps crisp nodal lines.
    const genPattern = () => {
      const pos: { x: number; y: number }[] = []
      const PI = Math.PI
      const RAD = SQUARE_HALF
      for (let x = -RAD; x <= RAD; x += GRID_STEP) {
        for (let y = -RAD; y <= RAD; y += GRID_STEP) {
          if (x * x + y * y > RAD * RAD) {
            continue
          }
          const nx = x / RAD,
            ny = y / RAD
          const u = (nx + 1) / 2,
            v = (ny + 1) / 2
          const sNu = Math.sin(PI * MODE_N * u),
            sMv = Math.sin(PI * MODE_M * v)
          const sMu = Math.sin(PI * MODE_M * u),
            sNv = Math.sin(PI * MODE_N * v)
          const cNu = Math.cos(PI * MODE_N * u),
            cMv = Math.cos(PI * MODE_M * v)
          const cMu = Math.cos(PI * MODE_M * u),
            cNv = Math.cos(PI * MODE_N * v)
          const val = sNu * sMv + sMu * sNv
          const dvdx =
            ((PI * MODE_N * cNu * sMv + PI * MODE_M * cMu * sNv) * 0.5) / RAD
          const dvdy =
            ((PI * MODE_M * sNu * cMv + PI * MODE_N * sMu * cNv) * 0.5) / RAD
          const gradMag = Math.sqrt(dvdx * dvdx + dvdy * dvdy)
          const dist = Math.abs(val) / (gradMag + 1e-6)
          if (dist < DIST_THRESHOLD) {
            pos.push({
              x: CX + x + (rand() - 0.5) * 1.5,
              y: CY + y + (rand() - 0.5) * 1.5,
            })
          }
        }
      }
      // Rim ring — the plate boundary, at the same density as the interior.
      const steps = Math.round((2 * PI * RAD) / GRID_STEP)
      for (let i = 0; i < steps; i++) {
        const a = (i / steps) * PI * 2
        pos.push({
          x: CX + Math.cos(a) * RAD + (rand() - 0.5) * 1.5,
          y: CY + Math.sin(a) * RAD + (rand() - 0.5) * 1.5,
        })
      }
      return pos
    }

    // ── ai4-hero particle physics ──────────────────────────────────────────
    // Particles do a biased random walk, stepping harder where the Chladni
    // amplitude is large and settling on the nodal lines; particles that reach
    // the rim stick to it.
    const VIBRATION = 0.007
    const MIN_WALK = 0.0003
    const ampAt = (x: number, y: number) => {
      const u = (x + 1) / 2,
        v = (y + 1) / 2
      return (
        Math.sin(Math.PI * MODE_N * u) * Math.sin(Math.PI * MODE_M * v) +
        Math.sin(Math.PI * MODE_M * u) * Math.sin(Math.PI * MODE_N * v)
      )
    }

    // patA anchors the callout chips and the red "fail" dots to fixed points on
    // the nodal lines; the gray field settles onto the nodes via the walk below.
    const patA = genPattern()

    const N = 6500

    interface Particle {
      lx: number
      ly: number // resolved-figure walk position, unit-disk coords
      badgeIdx: number
    }

    const spawnDisk = () => {
      const a = rand() * Math.PI * 2
      const r = Math.sqrt(rand())
      return { x: Math.cos(a) * r, y: Math.sin(a) * r }
    }
    const particles: Particle[] = []
    for (let i = 0; i < N; i++) {
      const l = spawnDisk()
      particles.push({ lx: l.x, ly: l.y, badgeIdx: 0 })
    }

    // ai4-style pointer push: pushEnergy spikes to 1 on movement and decays each
    // frame, so the cursor disturbs the walk (with a swirl) and the Chladni pull
    // heals it.
    let pushEnergy = 0
    let mPushActive = false
    let mLX = 0,
      mLY = 0
    const PUSH_INFL = 0.14
    const PUSH_FORCE = 0.06
    const PUSH_SWIRL = 0.45

    const applyPush = (
      x: number,
      y: number,
      mx: number,
      my: number,
    ): [number, number] => {
      if (!mPushActive || pushEnergy <= 0.002) {
        return [x, y]
      }
      const dx = x - mx,
        dy = y - my
      const dist = Math.hypot(dx, dy)
      if (dist >= PUSH_INFL) {
        return [x, y]
      }
      const f = (1 - dist / PUSH_INFL) * pushEnergy * PUSH_FORCE
      const inv = f / (dist + 1e-4)
      return [
        x + dx * inv - dy * inv * PUSH_SWIRL,
        y + dy * inv + dx * inv * PUSH_SWIRL,
      ]
    }

    const stepWalk = () => {
      for (const p of particles) {
        const stepSize = Math.max(
          MIN_WALK,
          VIBRATION * Math.abs(ampAt(p.lx, p.ly)),
        )
        let nx = p.lx + (Math.random() * 2 - 1) * stepSize
        let ny = p.ly + (Math.random() * 2 - 1) * stepSize
        ;[nx, ny] = applyPush(nx, ny, mLX, mLY)
        const d = Math.hypot(nx, ny)
        if (d > 1) {
          nx /= d
          ny /= d
        }
        p.lx = nx
        p.ly = ny
      }
    }
    // Settle the walk before first paint so the figure is already formed.
    for (let k = 0; k < 600; k++) {
      stepWalk()
    }

    const nearestPatternPoint = (tx: number, ty: number) => {
      let bestDist = Infinity,
        bestX = tx,
        bestY = ty
      for (const p of patA) {
        const d = (p.x - tx) ** 2 + (p.y - ty) ** 2
        if (d < bestDist) {
          bestDist = d
          bestX = p.x
          bestY = p.y
        }
      }
      return { x: bestX, y: bestY }
    }

    // Red error particles — lock onto nodal-line positions.
    const FAIL_POSITIONS = [
      { rx: 0.52, ry: 0.28 },
      { rx: 0.1, ry: 0.31 },
      { rx: 0.75, ry: 0.27 },
      { rx: 0.28, ry: 0.34 },
      { rx: 0.12, ry: 0.67 },
      { rx: 0.74, ry: 0.72 },
      { rx: 0.42, ry: 0.18 },
      { rx: 0.62, ry: 0.44 },
      { rx: 0.2, ry: 0.52 },
      { rx: 0.84, ry: 0.48 },
      { rx: 0.36, ry: 0.78 },
      { rx: 0.58, ry: 0.82 },
      { rx: 0.18, ry: 0.42 },
      { rx: 0.8, ry: 0.36 },
      { rx: 0.46, ry: 0.58 },
      { rx: 0.68, ry: 0.62 },
      { rx: 0.3, ry: 0.22 },
      { rx: 0.88, ry: 0.6 },
    ]

    interface RedParticle {
      targetX: number
      targetY: number
      jitterPhaseX: number
      jitterPhaseY: number
      jitterFreqX: number
      jitterFreqY: number
      badgeIdx: number
    }

    const redParticles: RedParticle[] = FAIL_POSITIONS.map((m) => {
      const snap = nearestPatternPoint(m.rx * 1100, m.ry * 1100)
      return {
        targetX: snap.x,
        targetY: snap.y,
        jitterPhaseX: rand() * Math.PI * 2,
        jitterPhaseY: rand() * Math.PI * 2,
        jitterFreqX: 0.9 + rand() * 0.8,
        jitterFreqY: 0.9 + rand() * 0.8,
        badgeIdx: 0,
      }
    })

    // Snap every labeled callout dot onto a unique pattern particle so each chip
    // dot overlaps a cymatic dot. MIN_SPACING_SQ reserves a radius around each
    // claimed point so chips can't pile up on the same nodal segment.
    const claimedSnaps: { x: number; y: number }[] = []
    const MIN_SPACING_SQ = 110 * 110
    const calloutData: { label: string; pass: boolean }[] = []
    const chips = Array.from(
      calloutsA.querySelectorAll<HTMLElement>("[data-callout]"),
    )
    chips.forEach((el) => {
      calloutData.push({
        label: el.dataset.label ?? "",
        pass: el.dataset.pass === "true",
      })
      const lRaw = el.style.getPropertyValue("--l")
      const tRaw = el.style.getPropertyValue("--t")
      if (!lRaw || !tRaw) {
        return
      }
      const tx = (parseFloat(lRaw) / 100) * 1100
      const ty = (parseFloat(tRaw) / 100) * 1100
      let best: { x: number; y: number } | null = null
      let bestDist = Infinity
      for (const p of patA) {
        let tooClose = false
        for (const c of claimedSnaps) {
          const dx = c.x - p.x,
            dy = c.y - p.y
          if (dx * dx + dy * dy < MIN_SPACING_SQ) {
            tooClose = true
            break
          }
        }
        if (tooClose) {
          continue
        }
        const d = (p.x - tx) ** 2 + (p.y - ty) ** 2
        if (d < bestDist) {
          bestDist = d
          best = { x: p.x, y: p.y }
        }
      }
      if (!best) {
        return
      }
      claimedSnaps.push(best)
      el.style.setProperty("--l", `${(best.x / 1100) * 100}%`)
      el.style.setProperty("--t", `${(best.y / 1100) * 100}%`)
    })

    // Spread the labelled badges across every particle so any hovered particle
    // shows one of the same pass/fail chips that appears in the resolved state.
    const failBadgeIdx = calloutData
      .map((b, i) => ({ b, i }))
      .filter((x) => !x.b.pass)
      .map((x) => x.i)
    if (calloutData.length > 0) {
      for (let i = 0; i < particles.length; i++) {
        particles[i].badgeIdx = i % calloutData.length
      }
    }
    if (failBadgeIdx.length > 0) {
      for (let i = 0; i < redParticles.length; i++) {
        redParticles[i].badgeIdx = failBadgeIdx[i % failBadgeIdx.length]
      }
    }

    const hoverLabelEl = hoverBadge.querySelector<HTMLElement>(
      "[data-callout-label]",
    )!

    // Canvas sizing — account for HiDPI displays.
    let canvasW = 0,
      canvasH = 0,
      scale = 1
    const particleR = 1.1

    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      canvasW = canvas.clientWidth
      canvasH = canvas.clientHeight
      canvas.width = canvasW * dpr
      canvas.height = canvasH * dpr
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      const sq = Math.min(canvasW, canvasH)
      scale = sq / 1100
      // Drive the measured viz-square size into CSS so the callout overlay
      // tracks the exact particle field.
      section.style.setProperty(
        "--cym-viz-size",
        `${Math.min(canvasW, canvasH) * END_SCALE}px`,
      )
    }

    resize()
    const onResize = () => resize()
    window.addEventListener("resize", onResize, { passive: true })

    let mouseClientX = -99999,
      mouseClientY = -99999
    const onMouseMove = (e: MouseEvent) => {
      mouseClientX = e.clientX
      mouseClientY = e.clientY
      pushEnergy = 1
    }
    const onMouseLeave = () => {
      mouseClientX = -99999
      mouseClientY = -99999
      pushEnergy = 0
    }
    window.addEventListener("mousemove", onMouseMove, { passive: true })
    document.addEventListener("mouseleave", onMouseLeave, { passive: true })

    // No scroll here: the figure renders permanently in its resolved state.
    const drawScale = END_SCALE

    // Cycle the metric callouts one at a time so the resolved figure reads as a
    // live feed of events rather than a wall of labels.
    chips.forEach((c, i) => {
      c.style.opacity = i === 0 ? "1" : "0"
    })
    let chipInterval: ReturnType<typeof setInterval> | undefined
    if (chips.length > 1) {
      let chipIdx = 0
      chipInterval = setInterval(() => {
        chips[chipIdx].style.opacity = "0"
        chipIdx = (chipIdx + 1) % chips.length
        chips[chipIdx].style.opacity = "1"
      }, 1500)
    }

    let rafId = 0
    let lastHoverLabel = ""
    let lastHoverClientX = -99999,
      lastHoverClientY = -99999
    const PASS_CLASS = styles.pass
    const FAIL_CLASS = styles.fail
    const VISIBLE_CLASS = styles.visible

    const frame = (ts: number) => {
      const t = ts * 0.00038

      const vizScale = scale * drawScale
      const vizOffsetX = canvasW / 2 - 550 * vizScale
      const vizOffsetY = canvasH / 2 - 550 * vizScale
      const vizR = particleR * END_SCALE

      const canvasRect = canvas.getBoundingClientRect()
      const mouseX = mouseClientX - canvasRect.left
      const mouseY = mouseClientY - canvasRect.top
      const mouseInBounds =
        mouseX >= 0 && mouseX <= canvasW && mouseY >= 0 && mouseY <= canvasH

      pushEnergy *= 0.8
      mPushActive = mouseInBounds && vizScale > 0
      if (mPushActive) {
        const px1100 = (mouseX - vizOffsetX) / vizScale
        const py1100 = (mouseY - vizOffsetY) / vizScale
        mLX = (px1100 - CX) / SQUARE_HALF
        mLY = (py1100 - CY) / SQUARE_HALF
      }

      stepWalk()

      const PUSH_R = 55
      const PUSH_R_SQ = PUSH_R * PUSH_R
      const PUSH_STRENGTH = 0.45

      const hoverEnabled = mouseInBounds && calloutData.length > 0
      const hoverHitR = canvasW <= 768 ? 16 : 22
      let hoverHitSq = hoverHitR * hoverHitR
      let hoverHit: { label: string; pass: boolean } | null = null

      ctx.clearRect(0, 0, canvasW, canvasH)
      // Canvas art: fillStyle can't resolve CSS vars, so these decorative
      // cloud tones are kept as literals by necessity.
      ctx.fillStyle = "#34332d"
      ctx.globalAlpha = 0.85 // resolved field reads a touch lighter than the raw cloud

      ctx.beginPath()
      for (const p of particles) {
        const patX = CX + p.lx * SQUARE_HALF
        const patY = CY + p.ly * SQUARE_HALF
        const cx = vizOffsetX + patX * vizScale
        const cy = vizOffsetY + patY * vizScale
        if (hoverEnabled) {
          const hdx = cx - mouseX,
            hdy = cy - mouseY
          const hd = hdx * hdx + hdy * hdy
          if (hd < hoverHitSq) {
            hoverHitSq = hd
            const b = calloutData[p.badgeIdx]
            hoverHit = { label: b.label, pass: b.pass }
          }
        }
        ctx.moveTo(cx + vizR, cy)
        ctx.arc(cx, cy, vizR, 0, Math.PI * 2)
      }
      ctx.fill()

      ctx.globalAlpha = 1
      ctx.fillStyle = "#C06A60"
      for (const rp of redParticles) {
        const settledX =
          rp.targetX + 2 * Math.sin(rp.jitterFreqX * t + rp.jitterPhaseX)
        const settledY =
          rp.targetY + 2 * Math.sin(rp.jitterFreqY * t + rp.jitterPhaseY)
        let cx = vizOffsetX + settledX * vizScale
        let cy = vizOffsetY + settledY * vizScale
        if (mouseInBounds) {
          const dx = cx - mouseX,
            dy = cy - mouseY
          const dSq = dx * dx + dy * dy
          if (dSq < PUSH_R_SQ) {
            const falloff = 1 - Math.sqrt(dSq) / PUSH_R
            const push = falloff * falloff * PUSH_STRENGTH
            cx += dx * push
            cy += dy * push
          }
        }
        if (hoverEnabled) {
          const hdx = cx - mouseX,
            hdy = cy - mouseY
          const hd = hdx * hdx + hdy * hdy
          if (hd < hoverHitSq) {
            hoverHitSq = hd
            const b = calloutData[rp.badgeIdx]
            hoverHit = { label: b.label, pass: false }
          }
        }
        ctx.beginPath()
        ctx.arc(cx, cy, vizR, 0, Math.PI * 2)
        ctx.fill()
      }

      // Badge follows the cursor; its label is only re-picked when the cursor
      // actually moves, so a held-still mouse doesn't cycle labels as particles
      // drift beneath it.
      const cursorMoved =
        mouseClientX !== lastHoverClientX || mouseClientY !== lastHoverClientY
      lastHoverClientX = mouseClientX
      lastHoverClientY = mouseClientY

      if (hoverHit) {
        if (
          (cursorMoved || lastHoverLabel === "") &&
          lastHoverLabel !== hoverHit.label
        ) {
          hoverLabelEl.textContent = hoverHit.label
          hoverBadge.classList.toggle(PASS_CLASS, hoverHit.pass)
          hoverBadge.classList.toggle(FAIL_CLASS, !hoverHit.pass)
          lastHoverLabel = hoverHit.label
        }
        hoverBadge.style.left = `${mouseX}px`
        hoverBadge.style.top = `${mouseY}px`
        if (!hoverBadge.classList.contains(VISIBLE_CLASS)) {
          hoverBadge.classList.add(VISIBLE_CLASS)
        }
      } else if (hoverBadge.classList.contains(VISIBLE_CLASS)) {
        hoverBadge.classList.remove(VISIBLE_CLASS)
      }

      rafId = requestAnimationFrame(frame)
    }

    rafId = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(rafId)
      if (chipInterval) {
        clearInterval(chipInterval)
      }
      window.removeEventListener("resize", onResize)
      window.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseleave", onMouseLeave)
    }
  }, [])

  return (
    <div ref={sectionRef} className={styles.cymatic}>
      <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />

      <div ref={calloutsRef} className={styles.callouts} aria-hidden="true">
        <div className={styles.calloutsInner}>
          {METRICS_A.map((m, i) => (
            <div
              key={i}
              data-callout
              data-label={m.label}
              data-pass={m.pass}
              className={`${styles.callout} ${m.pass ? styles.pass : styles.fail}`}
              style={
                {
                  "--l": `${m.rx * 100}%`,
                  "--t": `${m.ry * 100}%`,
                } as CSSProperties
              }
            >
              <span className={styles.calloutLabel}>{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div
        ref={hoverBadgeRef}
        className={`${styles.callout} ${styles.hoverBadge} ${styles.pass}`}
        aria-hidden="true"
      >
        <span data-callout-label className={styles.calloutLabel} />
      </div>
    </div>
  )
}
